import {useState, useCallback } from 'react'
import { Container, Row, Col, FloatingLabel, Form } from "react-bootstrap";
import "./style.css";
import Button from "../../components/Button";
import { formatDate } from "./helper";
import Checkbox from "../CheckBox";
import { PersonInterfaceForComponent as PersonData } from "../../models/GetCaseByCaseNumber";
import { getInitialsFromString } from "../../utils/helpers";
import Field from '../Field';

interface Person {
    person: PersonData;
    reports: any;
    isDisabled: boolean;
    updatePersonReportType: Function;
    updatePersonComments: Function;
    updateReceivedDate: Function;
    updateCollectedDate: Function;
    updateIsConditionValid: Function;
    updateIsDocumentationValid: Function;
    updateCompletedBy: Function;
    updateCompletedAt: Function;
    updateRace: Function;
    isForLegalReport: boolean;
    updateChainOfCustody: Function;
}

const Persons = (props: Person) => {
    const { 
        person, 
        updatePersonReportType, 
        updatePersonComments, 
        reports, 
        updateCollectedDate, 
        updateReceivedDate, 
        isDisabled, 
        updateIsDocumentationValid, 
        updateIsConditionValid, 
        isForLegalReport, 
        updateCompletedBy, 
        updateCompletedAt, 
        updateChainOfCustody,
        updateRace } = props;
    const [isCommentValidState, setIsCommentValidState] = useState(true);
    const getType = () => {
        let all = reports.reduce((prev: any, next: any) => prev.concat(next.reportPersons), []);
        let result = all.find((obj: any) => obj.personId === person.personId);
        if (typeof result === "undefined")
            return ""
        return result.type
    }

    const IsCommentValid = useCallback(() => {
        if(!person?.samples?.isConditionValid && !person?.samples?.comments) { setIsCommentValidState(false)}
        else {setIsCommentValidState(true)}
      }, [person?.samples?.comments, person?.samples?.isConditionValid]);

    // using js code from dymo.connect.framework.js imported in index.html
    // TODO Replace this code with the library react-dymo-hooks(https://www.npmjs.com/package/react-dymo-hooks) once the issue https://github.com/apenab/react-dymo/issues/41 is resolved.
    const handleDymoPrint = (labNumber: string, name: string, dob: string) => {
        let labelXml = `<?xml version="1.0" encoding="utf-8"?><DieCutLabel Version="8.0" Units="twips"><PaperOrientation>Portrait</PaperOrientation><Id>Small30334</Id><PaperName>30334 2-1/4 in x 1-1/4 in</PaperName><DrawCommands><RoundRectangle X="0" Y="0" Width="3240" Height="1800" Rx="270" Ry="270" /></DrawCommands><ObjectInfo><TextObject><Name>Text</Name><ForeColor Alpha="255" Red="0" Green="0" Blue="0" /><BackColor Alpha="0" Red="255" Green="255" Blue="255" /><LinkedObjectName></LinkedObjectName><Rotation>Rotation0</Rotation><IsMirrored>False</IsMirrored><IsVariable>True</IsVariable><HorizontalAlignment>Center</HorizontalAlignment><VerticalAlignment>Middle</VerticalAlignment><TextFitMode>ShrinkToFit</TextFitMode><UseFullFontHeight>True</UseFullFontHeight><Verticalized>False</Verticalized><StyledText><Element><String>${name} - ${formatDate(dob)}</String><Attributes><Font Family="Arial" Size="10" Bold="False" Italic="False" Underline="False" Strikeout="False" /><ForeColor Alpha="255" Red="0" Green="0" Blue="0" /></Attributes></Element></StyledText></TextObject><Bounds X="58" Y="86" Width="3125" Height="922" /></ObjectInfo><ObjectInfo><BarcodeObject><Name>Barcode</Name><ForeColor Alpha="255" Red="0" Green="0" Blue="0" /><BackColor Alpha="0" Red="255" Green="255" Blue="255" /><LinkedObjectName></LinkedObjectName><Rotation>Rotation0</Rotation><IsMirrored>False</IsMirrored><IsVariable>True</IsVariable><Text>${labNumber.toUpperCase()}</Text><Type>Code128A</Type><Size>Small</Size><TextPosition>Bottom</TextPosition><TextFont Family="Arial" Size="7.3125" Bold="False" Italic="False" Underline="False" Strikeout="False" /><CheckSumFont Family="Arial" Size="7.3125" Bold="False" Italic="False" Underline="False" Strikeout="False" /><TextEmbedding>None</TextEmbedding><ECLevel>0</ECLevel><HorizontalAlignment>Center</HorizontalAlignment><QuietZonesPadding Left="0" Top="0" Right="0" Bottom="0" /></BarcodeObject><Bounds X="58" Y="907.086627796303" Width="3123.77954101563" Height="600.944885253906" /></ObjectInfo></DieCutLabel>`;
        let label = dymo.label.framework.openLabelXml(labelXml);
        var printers = dymo.label.framework.getPrinters();
        var printerName = "";
        var isConnected = false;
        for (var i = 0; i < printers.length; ++i) {
            var printer = printers[i];
            if (printer.printerType === "LabelWriterPrinter") {
                printerName = printer.name;
                isConnected = printer.isConnected
                break;
            }
        }
        // Use following to test the barcode is generated properly 
        // var labelBase64 = label.render();
        // console.log(labelBase64);
        if (isConnected === true && printers.length > 0 && printerName !== "") {
            label.print(printerName);
        }
    };

    return (
        <Container style={{ background: "#ededed", padding: 10, borderRadius: 7, marginBottom: 15 }} fluid>
            <Row>
                <Col md={4} className="specimen-reception-label">
                    <Col>Full Name: <span className="specimen-reception-label-value">{person.name}</span></Col>
                    <Col md={3}>Gender: <span className="specimen-reception-label-value">{person.gender}</span></Col>
                    <Col md={5} className="specimen-reception-label">Date Of Birth: <span className="specimen-reception-label-value">{formatDate(person.dateOfBirth)}</span></Col>
                    <Col>
                        <Col style={{ marginTop: '15px' }}>
                            <Checkbox isDisabled={isDisabled} text="Sample received was in a good state." value={person?.samples?.isConditionValid ?? false} onChange={(val: boolean) => {updateIsConditionValid(val, person.personId); IsCommentValid()}}/>
                            <Checkbox isDisabled={isDisabled} text="Sample documentation was validated." value={person?.samples?.isDocumentationValid ?? false} onChange={(val: boolean) => updateIsDocumentationValid(val, person.personId)}/>
                            {isForLegalReport && <Checkbox isDisabled={isDisabled} text="Was chain of custody completed." value={person?.samples?.isChainOfCustodyCompleted ?? false} onChange={(val: boolean) => { updateChainOfCustody(val, person.personId) }} />}
                        </Col>
                    </Col>
                </Col>
                <Col md={4} className="specimen-reception-label">
                    <Col>Received Date: <input type="date" disabled={isDisabled} value={person?.samples?.receivedDate} onChange={(e) => updateReceivedDate(person.personId, e.currentTarget.value)} /></Col>
                    <Col style={{ paddingTop: 10 }} className="specimen-reception-label">Collected Date: <input disabled={isDisabled} type="date" value={person?.samples?.collectedDate} onChange={(e) => updateCollectedDate(person.personId, e.currentTarget.value)} /></Col>
                </Col>
                <Col md={4}>
                    <Col>
                        <Row>
                            <Col md={5}>
                                <Form.Select aria-label="Default select example" className="specimen-reception-label" disabled={isDisabled} value={getType() ?? "none"} onChange={(e) => updatePersonReportType(person.personId, e.currentTarget.value)}>
                                    <option value="none">Select Type</option>
                                    <option value="Alleged Father">Alleged Father</option>
                                    <option value="Alleged Mother">Alleged Mother</option>
                                    <option value="Child">Child</option>
                                    <option value="Mother">Mother</option>
                                </Form.Select>
                            </Col>
                            <Col>
                                <Col md={7} className="specimen-reception-label">
                                    <Button disabled={isDisabled} style={{ fontSize: 13, minWidth: 215 }} title={`Print ${person?.samples?.specimenNumber.toUpperCase()}-${getInitialsFromString(getType() ?? "none").toUpperCase()}`} handleClick={() => handleDymoPrint(`${person?.samples?.specimenNumber}-${getInitialsFromString(getType() ?? "none")}`, `${person.lastName}, ${person.firstName}`, person.dateOfBirth.toString())} /></Col>
                            </Col>
                        </Row>
                    </Col>
                    <Col style={{ paddingTop: 10 }}> <FloatingLabel controlId="floatingTextarea2" label="Comments" className="specimen-reception-label">
                        <Form.Control
                            onBlur={() => IsCommentValid()}
                            disabled={isDisabled}
                            as="textarea"
                            placeholder="Leave a comment here"
                            style={{ height: '80px', margin: "0px 0px", resize: "none", fontSize: 13 }}
                            value={person?.samples?.comments}
                            onChange={(e: any) => {updatePersonComments(person.personId, e.target.value); IsCommentValid()}}
                        />
                        {!isCommentValidState && <div className="comment-error">Comments are required as received sample was not in a good state</div>}
                    </FloatingLabel></Col>
                </Col>
            </Row>
            {isForLegalReport && <Row>
                <Col>
                    <Field
                        labelStyle={{fontSize: 13}}
                        title="Completed By"
                        value={person?.samples?.completedBy}
                        onChange={(e: any) => updateCompletedBy(e.target.value, person.personId)}
                        type="text"
                    />
                </Col>
                <Col>
                    <Field
                        labelStyle={{fontSize: 13}}
                        title="Completed At (location)"
                        value={person?.samples?.completedAt}
                        onChange={(e: any) => updateCompletedAt(e.target.value, person.personId)}
                        type="text"
                    />
                </Col>
                <Col>
                    <Field
                        labelStyle={{fontSize: 13}}
                        title="Race"
                        value={person?.race}
                        onChange={(e: any) => updateRace(e.target.value, person.personId)}
                        type="text"
                    />
                </Col>
            </Row>}
        </Container>
    );
}

export default Persons;
